/**
 * Sponsor Display Service
 * Handles filtering and formatting of sponsor data for display in client cards
 */

import { YEAR_CONFIG } from '../config/yearConfig';

/**
 * Check if sponsor has activity (non-zero data) for specific year data
 * @param {Object} yearData - Object with rooms, rn, revenue properties
 * @returns {boolean} True if any value > 0
 */
const hasActivity = (yearData) => {
  if (!yearData) return false;
  return yearData.rooms > 0 || yearData.rn > 0 || yearData.revenue > 0;
};

/**
 * Check if sponsor has activity in any year (2022-2026)
 * @param {Object} yearlyData - Object with year keys (2022-2026)
 * @returns {boolean} True if any year has activity
 */
const hasAnyYearActivity = (yearlyData) => {
  if (!yearlyData) return false;
  const start = Number(YEAR_CONFIG.baseYear);
  const end = Number(YEAR_CONFIG.lastDataYear);

  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return false;
  }

  const years = [];
  for (let year = start; year <= end; year++) {
    years.push(year);
  }

  return years.some(year => hasActivity(yearlyData[year]));
};

/**
 * Format sponsor for display: "[ConferenceName] [SponsorName]"
 * @param {Object} sponsor - Sponsor object with conferenceName and groupName
 * @returns {string} Formatted display name
 */
const formatSponsorDisplay = (sponsor) => {
  return `${sponsor.conferenceName} ${sponsor.groupName}`;
};

/**
 * Get year label for display
 * @param {string} selectedYear - Selected year ("2024", "All", etc.)
 * @returns {string} Display label ("2024" or "All Years")
 */
export const getYearLabel = (selectedYear) => {
  if (!selectedYear || selectedYear === 'All') {
    return 'All Years';
  }
  return selectedYear;
};

/**
 * Get filtered sponsors for a client based on conference/year selection
 * @param {string} clientName - Client name to filter by
 * @param {Array} sponsorData - Array of sponsor data objects
 * @param {string} selectedConference - Selected conference ("All Conferences" or specific conference)
 * @param {string} selectedYear - Selected year ("All" or specific year like "2024")
 * @returns {Object} Object containing filtered sponsors array and year label
 */
export const getFilteredSponsors = (clientName, sponsorData, selectedConference, selectedYear) => {
  if (!sponsorData || !Array.isArray(sponsorData) || sponsorData.length === 0) {
    return {
      sponsors: [],
      yearLabel: getYearLabel(selectedYear)
    };
  }

  // Step 1: Filter by client name (customerName)
  let filtered = sponsorData.filter(sponsor => 
    sponsor.customerName === clientName
  );

  // Step 2: Filter by conference if specific conference selected
  if (selectedConference && selectedConference !== 'All Conferences' && selectedConference !== 'All') {
    filtered = filtered.filter(sponsor => 
      sponsor.conferenceName === selectedConference
    );
  }

  // Step 3: Filter by year/activity
  if (selectedYear && selectedYear !== 'All') {
    // Specific year selected - only show sponsors with activity in that year
    // Convert year to number to ensure proper key matching
    const yearNum = parseInt(selectedYear, 10);
    filtered = filtered.filter(sponsor => {
      const yearData = sponsor.yearlyData?.[yearNum];
      return hasActivity(yearData);
    });
  } else {
    // "All Years" selected - show sponsors with activity in any year
    filtered = filtered.filter(sponsor => 
      hasAnyYearActivity(sponsor.yearlyData)
    );
  }

  // Step 4: Sort alphabetically by conference name
  filtered.sort((a, b) => {
    const confA = a.conferenceName || '';
    const confB = b.conferenceName || '';
    return confA.localeCompare(confB);
  });

  // Step 5: Format for display
  const formattedSponsors = filtered.map(sponsor => ({
    displayName: formatSponsorDisplay(sponsor),
    conferenceName: sponsor.conferenceName,
    groupName: sponsor.groupName
  }));

  return {
    sponsors: formattedSponsors,
    yearLabel: getYearLabel(selectedYear)
  };
};
